import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import portalInvoiceService from '../../services/portalInvoiceService';
import { ArrowLeft, CreditCard, CheckCircle, AlertCircle, Calendar, Clock } from 'lucide-react';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';

function PortalInvoiceDetail() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const { notification, showError, showInfo, hideNotification } = useNotification();

  useEffect(() => {
    async function load() {
      try {
        const [invoiceRes, planRes] = await Promise.all([
          portalInvoiceService.getInvoiceDetail(invoiceId),
          portalInvoiceService.getPaymentPlan(invoiceId).catch(() => ({ data: { payment_plan: null } })),
        ]);
        setInvoice(invoiceRes.data);
        setPaymentPlan(planRes.data.payment_plan);
      } catch (err) {
        console.error('Failed to load invoice:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [invoiceId]);

  const handlePayNow = async () => {
    setPayLoading(true);
    try {
      const response = await portalInvoiceService.getPaymentLink(invoiceId);
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      } else {
        showInfo(response.data.message || 'No payment link available.');
      }
    } catch (err) {
      console.error('Failed to get payment link:', err);
      showError('Unable to process payment at this time.');
    } finally {
      setPayLoading(false);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const getInstallmentStatusStyle = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': case 'processing': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': case 'skipped': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isNextDue = (inst) => {
    if (!paymentPlan) return false;
    return inst.due_date === paymentPlan.next_payment_date && ['scheduled', 'pending'].includes(inst.status);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Invoice not found.</p>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';
  const isPayable = ['sent', 'overdue', 'partial'].includes(invoice.status);

  return (
    <div>
      <Link
        to="/portal/invoices"
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Invoices
      </Link>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Invoice #{invoice.invoice_number}</h1>
              {invoice.case_title && (
                <p className="text-sm text-gray-500 mt-1">{invoice.case_title}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(invoice.total_amount)}</p>
              {invoice.balance_due > 0 && (
                <p className="text-sm text-orange-600 mt-1">Balance due: {formatCurrency(invoice.balance_due)}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
            <span>Issued: {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}</span>
            <span>Due: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</span>
            {isPaid && (
              <span className="inline-flex items-center gap-1 text-green-600">
                <CheckCircle className="h-4 w-4" /> Paid
              </span>
            )}
            {invoice.status === 'overdue' && (
              <span className="inline-flex items-center gap-1 text-red-600">
                <AlertCircle className="h-4 w-4" /> Overdue
              </span>
            )}
          </div>

          {isPayable && !paymentPlan && (
            <button
              onClick={handlePayNow}
              disabled={payLoading}
              className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <CreditCard className="h-4 w-4" />
              {payLoading ? 'Loading...' : 'Pay Now'}
            </button>
          )}
        </div>

        {/* Payment Plan Section */}
        {paymentPlan && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h2 className="text-sm font-semibold text-gray-900">Payment Plan</h2>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                paymentPlan.status === 'active' ? 'bg-green-100 text-green-800' :
                paymentPlan.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {paymentPlan.status.charAt(0).toUpperCase() + paymentPlan.status.slice(1)}
              </span>
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-gray-600">
                  {paymentPlan.installments_completed} of {paymentPlan.number_of_installments} payments made
                </span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(paymentPlan.total_paid)} of {formatCurrency(paymentPlan.total_amount)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min((paymentPlan.total_paid / paymentPlan.total_amount) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Installment schedule */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">#</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Due Date</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(paymentPlan.installments || []).map((inst) => (
                    <tr
                      key={inst.id}
                      className={`border-b border-gray-100 last:border-b-0 ${isNextDue(inst) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="py-2 px-3 text-gray-600">{inst.installment_number}</td>
                      <td className="py-2 px-3 text-gray-900">
                        {inst.due_date ? new Date(inst.due_date + 'T00:00:00').toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-gray-900">
                        {formatCurrency(inst.amount)}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getInstallmentStatusStyle(inst.status)}`}>
                          {inst.status === 'completed' && <CheckCircle className="h-3 w-3" />}
                          {(inst.status === 'scheduled' || inst.status === 'pending') && <Clock className="h-3 w-3" />}
                          {inst.status.charAt(0).toUpperCase() + inst.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Next payment callout */}
            {paymentPlan.status === 'active' && paymentPlan.next_payment_date && isPayable && (
              <div className="mt-4 flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium text-blue-900">Next payment: </span>
                  <span className="text-blue-800">
                    {formatCurrency(paymentPlan.installment_amount)} due {new Date(paymentPlan.next_payment_date + 'T00:00:00').toLocaleDateString()}
                  </span>
                  {paymentPlan.auto_charge && (
                    <span className="text-blue-600 ml-1">(auto-charge enabled)</span>
                  )}
                </div>
                {!paymentPlan.auto_charge && (
                  <button
                    onClick={handlePayNow}
                    disabled={payLoading}
                    className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <CreditCard className="h-3.5 w-3.5" />
                    {payLoading ? 'Loading...' : 'Pay Now'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Client notes */}
        {invoice.client_notes && (
          <div className="p-6 border-b border-gray-200 bg-blue-50">
            <p className="text-sm font-medium text-blue-800 mb-1">Note from your attorney:</p>
            <p className="text-sm text-blue-700 whitespace-pre-wrap">{invoice.client_notes}</p>
          </div>
        )}

        {/* Line items */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Line Items</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Description</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-600">Qty/Hours</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-600">Rate</th>
                  <th className="text-right py-2 pl-2 font-medium text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items || []).map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{item.description}</td>
                    <td className="py-2 px-2 text-right text-gray-600">
                      {item.hours ? `${item.hours}h` : item.quantity || ''}
                    </td>
                    <td className="py-2 px-2 text-right text-gray-600">
                      {item.rate ? formatCurrency(item.rate) : item.unit_price ? formatCurrency(item.unit_price) : ''}
                    </td>
                    <td className="py-2 pl-2 text-right font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {invoice.tax_amount > 0 && (
                  <tr className="border-t border-gray-200">
                    <td colSpan="3" className="py-2 text-right text-gray-600">Tax</td>
                    <td className="py-2 pl-2 text-right text-gray-900">{formatCurrency(invoice.tax_amount)}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-gray-300">
                  <td colSpan="3" className="py-2 text-right font-semibold text-gray-900">Total</td>
                  <td className="py-2 pl-2 text-right font-bold text-gray-900">{formatCurrency(invoice.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Payment history */}
        {(invoice.payments || []).length > 0 && (
          <div className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Payment History</h2>
            <div className="space-y-2">
              {invoice.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {p.payment_date ? new Date(p.payment_date).toLocaleDateString() : 'N/A'}
                    {p.payment_method && ` · ${p.payment_method}`}
                  </span>
                  <span className="font-medium text-green-600">{formatCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Notification {...notification} onClose={hideNotification} />
    </div>
  );
}

export default PortalInvoiceDetail;
